import { PagesId } from '@ts-enums/PagesId'
import { Translations } from '@ts-types/Translations'

const globalTranslations: Translations = {
  es: {
    title: 'Pedidos',
    description:
      'Resuelve dudas sobre compras, ventas, envíos, pagos y reembolsos. Aprende cómo realizar órdenes y adjuntar documentos importantes.',
    seo_keywords:
      'pedidos, gestión de pedidos, estados de pedidos, pedido en borrador, pedido preparado, pedido entregado, pedido cancelado, plan freemium, plan premium, historial de pedidos, pedidos internacionales, cancelar pedido, tipos de productos, ventas, compras, envíos, pagos, reembolsos, documentos de pedidos',
    managing_orders: 'Gestión de Pedidos',
    payments_and_docs: 'Pagos y Documentos',
    page_id: PagesId.OrdersHome,
    breadcrumbs: {
      home: 'Inicio',
      orders_home: 'Pedidos',
    },
    faq: [
      {
        question: '¿Por qué no puedo ver mis pedidos anteriores?',
        answer:
          'En el plan Freemium, el historial de pedidos solo está disponible por un tiempo limitado debido a la política de almacenamiento del plan. Al actualizar al plan Premium, tendrás acceso ilimitado a todos tus pedidos anteriores.',
      },
      {
        question: '¿Qué estados de pedido están disponibles en wicadu?',
        answer: `
          <p>En wicadu, los pedidos tienen estados simples para que todo sea rápido y fácil. Estos son los estados que puedes asignale a tus pedidos: </p>
          <ul>
            <li><strong>Borrador:</strong> El pedido se está creando.</li>
            <li><strong>Preparado:</strong> El pedido ya está listo.</li>
            <li><strong>Entregado:</strong> El pedido se entrego al usuario.</li>
            <li><strong>Cancelado:</strong>  El pedido ha sido anulado y no seguirá en proceso.</li>
          </ul>
        `,
      },
      {
        question: '¿Puedo usar wicadu para pedidos internacionales?',
        answer:
          'Por el momento, wicadu está diseñado principalmente para ventas dentro del país, aunque en el futuro se incorporara soporte para pedidos internacionales.',
      },
      {
        question: '¿Cómo puedo eliminar un pedido?',
        answer:
          'No es posible eliminar un pedido, pero sí puedes marcarlo como CANCELADO para actualizar el inventario e inhabilitarlo.',
      },
      {
        question: '¿Qué tipos de productos puedo vender en un pedido?',
        answer:
          'wicadu permite vender todo tipo de productos, siempre que cumplan con los términos y condiciones de la plataforma. Esto incluye alimentos, muebles, electrónica y más.',
      },
    ],
  },
  en: {
    title: 'Orders',
    description:
      'Find answers about purchases, sales, shipping, payments, and refunds. Learn how to place orders and attach important documents.',
    page_id: PagesId.OrdersHome,
    seo_keywords:
      'orders, order management, order statuses, draft order, prepared order, delivered order, cancelled order, freemium plan, premium plan, order history, cross-border orders, international orders, cancel order, types of products, sales, purchases, shipping, payments, refunds, order documents',
    managing_orders: 'Order Management',
    payments_and_docs: 'Payments and Documents',
    breadcrumbs: {
      home: 'Home',
      orders_home: 'Orders',
    },
    faq: [
      {
        question: "Why can't I see my past orders?",
        answer: `In the Freemium plan, order history is only available for a limited time due to the plan's storage policy. Upgrading to the Premium plan gives you unlimited access to all your past orders.`,
      },
      {
        question: 'What order statuses are available in wicadu?',
        answer: `
          <p>In wicadu, orders have simple statuses to keep things fast and easy. These are the statuses you can assign to your order:</p>
          <ul>
            <li><strong>Draft:</strong> Order is being created.</li>
            <li><strong>Prepared:</strong> Order is ready.</li>
            <li><strong>Delivered:</strong> Order has been successfully delivered to the customer.</li>
            <li><strong>Cancelled:</strong> Order has been voided.</li>
          </ul>
        `,
      },
      {
        question: 'Does wicadu support cross-border orders?',
        answer: `Currently, wicadu is primarily designed for domestic sales, but support for international orders may be added in the future.`,
      },
      {
        question: 'How can I delete an order?',
        answer: `Orders cannot be deleted, but they can be marked as Cancelled. If you no longer need an order, you can change its status to CANCELLED to disable it and update the store's inventory.`,
      },
      {
        question: 'What types of items can I sell in an order?',
        answer: `wicadu allows the sale of all kinds of products, as long as they comply with the platform's terms and conditions. This includes food, furniture, electronics, and more.`,
      },
    ],
  },
  fr: {
    title: 'Commandes',
    description:
      "Trouvez des réponses sur les achats, les ventes, l'expédition, les paiements et les remboursements. Découvrez comment créer des commandes et joindre des documents importants.",
    page_id: PagesId.OrdersHome,
    seo_keywords:
      "commandes, gestion des commandes, statuts des commandes, commande brouillon, commande préparée, commande livrée, commande annulée, plan freemium, plan premium, historique des commandes, ventes, achats, expédition, paiements, remboursements, documents de commande",
    managing_orders: 'Gestion des commandes',
    payments_and_docs: 'Paiements et documents',
    breadcrumbs: {
      home: 'Accueil',
      orders_home: 'Commandes',
    },
    faq: [
      {
        question: "Pourquoi ne puis-je pas voir mes anciennes commandes ?",
        answer: "Avec le plan Freemium, l'historique des commandes n'est disponible que pendant une durée limitée en raison de la politique de stockage du plan. Le passage au plan Premium vous donne un accès illimité à toutes vos anciennes commandes.",
      },
      {
        question: 'Quels statuts de commande sont disponibles dans wicadu ?',
        answer: `
          <p>Dans wicadu, les commandes ont des statuts simples afin que tout reste rapide et facile. Voici les statuts que vous pouvez attribuer à une commande :</p>
          <ul>
            <li><strong>Brouillon :</strong> la commande est en cours de création.</li>
            <li><strong>Préparée :</strong> la commande est prête.</li>
            <li><strong>Livrée :</strong> la commande a été livrée avec succès au client.</li>
            <li><strong>Annulée :</strong> la commande a été annulée.</li>
          </ul>
        `,
      },
      {
        question: 'wicadu prend-il en charge les commandes internationales ?',
        answer: "Actuellement, wicadu est principalement conçu pour les ventes nationales, mais la prise en charge des commandes internationales pourra être ajoutée à l'avenir.",
      },
      {
        question: 'Comment puis-je supprimer une commande ?',
        answer: "Les commandes ne peuvent pas être supprimées, mais elles peuvent être marquées comme annulées. Si vous n'avez plus besoin d'une commande, vous pouvez changer son statut en ANNULÉE afin de la désactiver et de mettre à jour l'inventaire de la boutique.",
      },
      {
        question: 'Quels types de produits puis-je vendre dans une commande ?',
        answer: "wicadu permet de vendre toutes sortes de produits, à condition qu'ils respectent les termes et conditions de la plateforme. Cela inclut les aliments, les meubles, l'électronique et plus encore.",
      },
    ],
  },
  pt: {},
}


export default globalTranslations
